import React, { useEffect, useState, useCallback } from 'react';
import { Header } from '../../components/layout/Header';
import { journeyApi } from '../../lib/api';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { 
    CheckCircle, 
    Circle,
    Target,
    Linkedin,
    Mail,
    Megaphone,
    TrendingUp,
    Calendar,
    Pencil,
    Plus,
    Trash2,
    Save,
    X,
    GripVertical
} from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';

const channelIcons = {
    'Strategy': Target,
    'LinkedIn': Linkedin,
    'Email': Mail,
    'Ads': Megaphone,
    'Inbound': TrendingUp,
};

const AVAILABLE_CHANNELS = ['Strategy', 'LinkedIn', 'Email', 'Ads', 'Inbound'];
const STATUS_OPTIONS = [
    { value: 'completed', label: 'Completed', color: 'bg-status-success' },
    { value: 'in_progress', label: 'In Progress', color: 'bg-brand-red' },
    { value: 'not_started', label: 'Not Started', color: 'bg-surface-card' },
];

function PhaseEditor({ phase, onSave, onCancel, onDelete, isNew }) {
    const [form, setForm] = useState({
        name: phase?.name || '',
        week_start: phase?.week_start || 1,
        week_end: phase?.week_end || 2,
        objective: phase?.objective || '',
        activities: phase?.activities || [],
        channels: phase?.channels || [],
        expected_outcomes: phase?.expected_outcomes || [],
        is_completed: phase?.is_completed || false,
    });
    const [activityInput, setActivityInput] = useState('');



    const getStatus = () => {
        if (form.is_completed) return 'completed';
        return 'not_started';
    };

    const setStatus = (status) => {
        setForm(prev => ({ ...prev, is_completed: status === 'completed' }));
    };

    const addActivity = () => {
        if (activityInput.trim()) {
            setForm(prev => ({ ...prev, activities: [...prev.activities, activityInput.trim()] }));
            setActivityInput('');
        }
    };

    const removeActivity = (idx) => {
        setForm(prev => ({ ...prev, activities: prev.activities.filter((_, i) => i !== idx) }));
    };



    const toggleChannel = (channel) => {
        setForm(prev => ({
            ...prev,
            channels: prev.channels.includes(channel)
                ? prev.channels.filter(c => c !== channel)
                : [...prev.channels, channel]
        }));
    };

    const handleSave = () => {
        if (!form.name.trim()) {
            toast.error('Phase name is required');
            return;
        }
        if (form.week_start > form.week_end) {
            toast.error('Start week must be before end week');
            return;
        }
        onSave({
            ...phase,
            ...form,
            id: phase?.id || undefined,
        });
    };

    return (
        <div className="p-5 rounded-lg border border-brand-red/40 bg-brand-red/5 space-y-4" data-testid="phase-editor">
            <div className="flex items-center justify-between">
                <h4 className="text-sm font-bold text-brand-red uppercase tracking-wider">
                    {isNew ? 'Add New Phase' : 'Edit Phase'}
                </h4>
                <div className="flex gap-2">
                    {!isNew && onDelete && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                            onClick={onDelete}
                            data-testid="delete-phase-btn"
                        >
                            <Trash2 className="w-4 h-4 mr-1" /> Remove
                        </Button>
                    )}
                    <Button
                        variant="ghost"
                        size="sm"
                        className="text-content-muted hover:text-white"
                        onClick={onCancel}
                        data-testid="cancel-phase-btn"
                    >
                        <X className="w-4 h-4" />
                    </Button>
                </div>
            </div>

            {/* Name */}
            <div>
                <label className="text-xs text-content-muted uppercase tracking-wider block mb-1">Phase Name</label>
                <Input
                    value={form.name}
                    onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                    className="bg-surface-app border-line-default text-white"
                    placeholder="e.g., Strategy & ICP Definition"
                    data-testid="phase-name-input"
                />
            </div>

            {/* Week Range */}
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="text-xs text-content-muted uppercase tracking-wider block mb-1">Start Week</label>
                    <Input
                        type="number"
                        min={1}
                        value={form.week_start}
                        onChange={e => {
                            const val = e.target.value;
                            setForm(prev => ({ ...prev, week_start: val === '' ? '' : parseInt(val) || prev.week_start }));
                        }}
                        onBlur={() => setForm(prev => ({ ...prev, week_start: prev.week_start || 1 }))}
                        className="bg-surface-app border-line-default text-white"
                        data-testid="phase-week-start-input"
                    />
                </div>
                <div>
                    <label className="text-xs text-content-muted uppercase tracking-wider block mb-1">End Week</label>
                    <Input
                        type="number"
                        min={1}
                        value={form.week_end}
                        onChange={e => {
                            const val = e.target.value;
                            setForm(prev => ({ ...prev, week_end: val === '' ? '' : parseInt(val) || prev.week_end }));
                        }}
                        onBlur={() => setForm(prev => ({ ...prev, week_end: prev.week_end || 1 }))}
                        className="bg-surface-app border-line-default text-white"
                        data-testid="phase-week-end-input"
                    />
                </div>
            </div>

            {/* Objective */}
            <div>
                <label className="text-xs text-content-muted uppercase tracking-wider block mb-1">Objective</label>
                <textarea
                    value={form.objective}
                    onChange={e => setForm(prev => ({ ...prev, objective: e.target.value }))}
                    className="w-full bg-surface-app border border-line-default text-white text-sm rounded-md p-2 focus:border-brand-red focus:ring-1 focus:ring-brand-red resize-none"
                    rows={2}
                    placeholder="Define the objective for this phase..."
                    data-testid="phase-objective-input"
                />
            </div>

            {/* Status */}
            <div>
                <label className="text-xs text-content-muted uppercase tracking-wider block mb-2">Status</label>
                <div className="flex gap-2">
                    {STATUS_OPTIONS.map(opt => (
                        <button
                            key={opt.value}
                            onClick={() => setStatus(opt.value)}
                            className={cn(
                                'px-3 py-1.5 rounded-md text-xs font-bold border transition-colors',
                                getStatus() === opt.value
                                    ? `${opt.color} border-transparent text-white`
                                    : 'bg-surface-card border-line-default text-content-muted hover:text-white'
                            )}
                            data-testid={`status-${opt.value}-btn`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Channels */}
            <div>
                <label className="text-xs text-content-muted uppercase tracking-wider block mb-2">Channels</label>
                <div className="flex flex-wrap gap-2">
                    {AVAILABLE_CHANNELS.map(ch => {
                        const Icon = channelIcons[ch] || Target;
                        const isSelected = form.channels.includes(ch);
                        return (
                            <button
                                key={ch}
                                onClick={() => toggleChannel(ch)}
                                className={cn(
                                    'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border transition-colors',
                                    isSelected
                                        ? 'bg-brand-red/20 border-brand-red text-white'
                                        : 'bg-surface-card border-line-default text-content-muted hover:text-white hover:border-content-muted'
                                )}
                                data-testid={`channel-toggle-${ch.toLowerCase()}`}
                            >
                                <Icon className="w-3 h-3" />
                                {ch}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Activities */}
            <div>
                <label className="text-xs text-content-muted uppercase tracking-wider block mb-2">Activities</label>
                <div className="flex gap-2 mb-2">
                    <Input
                        value={activityInput}
                        onChange={e => setActivityInput(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && addActivity()}
                        className="bg-surface-app border-line-default text-white flex-1"
                        placeholder="Add activity and press Enter..."
                        data-testid="activity-input"
                    />
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={addActivity}
                        className="text-brand-red hover:bg-brand-red/10"
                        data-testid="add-activity-btn"
                    >
                        <Plus className="w-4 h-4" />
                    </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                    {form.activities.map((act, i) => (
                        <span
                            key={`act-${act}-${i}`}
                            className="flex items-center gap-1 px-2 py-1 rounded bg-surface-card text-xs text-content-secondary border border-line-default"
                        >
                            {act}
                            <button onClick={() => removeActivity(i)} className="text-content-muted hover:text-red-400 ml-1">
                                <X className="w-3 h-3" />
                            </button>
                        </span>
                    ))}
                </div>
            </div>


            {/* Save Button */}
            <div className="flex justify-end pt-2">
                <Button
                    onClick={handleSave}
                    className="bg-brand-red hover:bg-brand-red/80 text-white font-bold gap-2"
                    data-testid="save-phase-btn"
                >
                    <Save className="w-4 h-4" />
                    {isNew ? 'Add Phase' : 'Save Changes'}
                </Button>
            </div>
        </div>
    );
}

export default function ABMJourneyPage() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [journey, setJourney] = useState(null);
    const [duration, setDuration] = useState('3');
    const [editingPhaseId, setEditingPhaseId] = useState(null);
    const [isAddingPhase, setIsAddingPhase] = useState(false);

    useEffect(() => {
        loadJourney();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadJourney = async () => {
        try {
            const response = await journeyApi.get();
            setJourney(response.data);
            if (response.data?.duration_months) {
                setDuration(String(response.data.duration_months));
            }
        } catch (error) {
            console.error('Failed to load journey');
        } finally {
            setLoading(false);
        }
    };

    const saveJourney = useCallback(async (updatedPhases) => {
        setSaving(true);
        try {
            const payload = {
                phases: updatedPhases.map(p => ({
                    id: p.id || undefined,
                    name: p.name,
                    week_start: p.week_start,
                    week_end: p.week_end,
                    objective: p.objective,
                    activities: p.activities || [],
                    channels: p.channels || [],
                    expected_outcomes: p.expected_outcomes || [],
                    is_completed: p.is_completed || false,
                })),
                current_week: journey?.current_week || 1,
                duration_months: parseInt(duration),
            };
            const response = await journeyApi.update(payload);
            setJourney(response.data);
            toast.success('Journey updated successfully');
        } catch (error) {
            console.error('Save error:', error);
            const msg = error?.response?.data?.detail || 'Failed to save journey';
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    }, [journey, duration]);

    const handleSavePhase = (updatedPhase) => {
        const phases = [...(journey?.phases || [])];
        const idx = phases.findIndex(p => p.id === updatedPhase.id);
        if (idx >= 0) {
            phases[idx] = updatedPhase;
        } else {
            phases.push(updatedPhase);
        }
        saveJourney(phases);
        setEditingPhaseId(null);
        setIsAddingPhase(false);
    };

    const handleDeletePhase = (phaseId) => {
        const phases = (journey?.phases || []).filter(p => p.id !== phaseId);
        saveJourney(phases);
        setEditingPhaseId(null);
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-brand-red/30 border-t-brand-red rounded-full animate-spin" />
            </div>
        );
    }

    const phases = journey?.phases || [];
    const currentWeek = journey?.current_week || 1;
    const completedPhases = phases.filter(p => p.is_completed).length;
    const totalPhases = phases.length;
    const totalWeeks = duration === '3' ? 12 : 24;
    const progress = totalPhases > 0 ? (completedPhases / totalPhases) * 100 : 0;

    const currentPhase = phases.find(
        p => !p.is_completed && currentWeek >= p.week_start && currentWeek <= p.week_end
    ) || phases.find(p => !p.is_completed);

    return (
        <div className="min-h-screen" data-testid="abm-journey-page">
            <Header 
                title="ABM Journey"
                subtitle="Your campaign roadmap and milestones"
            />

            <div className="p-8 space-y-8">
                {/* Progress Overview */}
                <section className="card-base p-6">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h3 className="text-lg font-heading font-bold text-white mb-1">
                                CAMPAIGN PROGRESS
                            </h3>
                            <p className="text-sm text-content-secondary">
                                Week {currentWeek} of {totalWeeks} weeks
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            <Tabs value={duration} onValueChange={setDuration}>
                                <TabsList className="bg-surface-app border border-line-default">
                                    <TabsTrigger 
                                        value="3"
                                        className="data-[state=active]:bg-brand-red data-[state=active]:text-white"
                                    >
                                        3 Months
                                    </TabsTrigger>
                                    <TabsTrigger 
                                        value="6"
                                        className="data-[state=active]:bg-brand-red data-[state=active]:text-white"
                                    >
                                        6 Months
                                    </TabsTrigger>
                                </TabsList>
                            </Tabs>
                        </div>
                    </div>

                    {/* Progress bar */}
                    <div className="relative mb-8">
                        <div className="h-3 bg-surface-app rounded-full overflow-hidden">
                            <div 
                                className="h-full rounded-full bg-gradient-to-r from-brand-red to-status-success transition-all duration-500"
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                        {totalWeeks > 0 && (
                            <div 
                                className="absolute top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-brand-red border-2 border-surface-app shadow-lg transition-all duration-500"
                                style={{ left: `calc(${Math.min((currentWeek / totalWeeks) * 100, 100)}% - 10px)` }}
                            />
                        )}
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-4 gap-4">
                        <div className="text-center p-4 rounded-lg bg-surface-app">
                            <p className="text-2xl font-heading font-bold text-white">{completedPhases}</p>
                            <p className="text-xs text-content-muted uppercase">Completed</p>
                        </div>
                        <div className="text-center p-4 rounded-lg bg-surface-app">
                            <p className="text-2xl font-heading font-bold text-brand-red">{currentPhase ? 1 : 0}</p>
                            <p className="text-xs text-content-muted uppercase">In Progress</p>
                        </div>
                        <div className="text-center p-4 rounded-lg bg-surface-app">
                            <p className="text-2xl font-heading font-bold text-white">
                                {Math.max(totalPhases - completedPhases - (currentPhase ? 1 : 0), 0)}
                            </p>
                            <p className="text-xs text-content-muted uppercase">Upcoming</p>
                        </div>
                        <div className="text-center p-4 rounded-lg bg-surface-app">
                            <p className="text-2xl font-heading font-bold text-status-success">{progress.toFixed(0)}%</p>
                            <p className="text-xs text-content-muted uppercase">Progress</p>
                        </div>
                    </div>
                </section>

                {/* Current Phase Highlight */}
                {currentPhase && editingPhaseId !== currentPhase.id && (
                    <section className="card-base p-6 border-l-4 border-l-brand-red">
                        <div className="flex items-start justify-between">
                            <div>
                                <Badge className="badge badge-error mb-2">CURRENT PHASE</Badge>
                                <h3 className="text-xl font-heading font-bold text-white mb-2">
                                    {currentPhase.name}
                                </h3>
                                <p className="text-content-secondary">
                                    {currentPhase.objective}
                                </p>
                            </div>
                            <div className="flex items-center gap-3">
                                <p className="text-sm text-content-muted">
                                    Week {currentPhase.week_start} - {currentPhase.week_end}
                                </p>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-content-muted hover:text-brand-red"
                                    onClick={() => setEditingPhaseId(currentPhase.id)}
                                    data-testid="edit-current-phase-btn"
                                >
                                    <Pencil className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    </section>
                )}

                {/* Timeline / Roadmap */}
                <section className="card-base p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-heading font-bold text-white">
                            CAMPAIGN ROADMAP
                        </h3>
                        <Button
                            onClick={() => {
                                setIsAddingPhase(true);
                                setEditingPhaseId(null);
                            }}
                            className="bg-brand-red hover:bg-brand-red/80 text-white font-bold gap-2"
                            disabled={saving || isAddingPhase}
                            data-testid="add-phase-btn"
                        >
                            <Plus className="w-4 h-4" />
                            Add Phase
                        </Button>
                    </div>

                    {/* Add Phase at top */}
                    {isAddingPhase && (
                        <div className="mb-6">
                            <PhaseEditor
                                phase={null}
                                isNew={true}
                                onSave={handleSavePhase}
                                onCancel={() => setIsAddingPhase(false)}
                            />
                        </div>
                    )}

                    <div className="space-y-0">
                        {phases.map((phase, index) => {
                            const isCurrent = currentPhase?.id === phase.id && !phase.is_completed;
                            const isPast = phase.is_completed;
                            const isFuture = !phase.is_completed && !isCurrent;
                            const isEditing = editingPhaseId === phase.id;

                            if (isEditing) {
                                return (
                                    <div key={phase.id} className="relative pl-8 pb-8">
                                        {index < phases.length - 1 && (
                                            <div className="absolute left-[11px] top-6 w-0.5 h-[calc(100%-24px)] bg-line-default" />
                                        )}
                                        <div className="absolute left-0 top-1 w-6 h-6 rounded-full flex items-center justify-center border-2 bg-brand-red border-brand-red">
                                            <Pencil className="w-3 h-3 text-white" />
                                        </div>
                                        <PhaseEditor
                                            phase={phase}
                                            isNew={false}
                                            onSave={handleSavePhase}
                                            onCancel={() => setEditingPhaseId(null)}
                                            onDelete={() => handleDeletePhase(phase.id)}
                                        />
                                    </div>
                                );
                            }

                            return (
                                <div 
                                    key={phase.id}
                                    className={cn(
                                        'relative pl-8 pb-8',
                                        index === phases.length - 1 && 'pb-0'
                                    )}
                                    data-testid={`phase-${phase.id}`}
                                >
                                    {/* Timeline line */}
                                    {index < phases.length - 1 && (
                                        <div 
                                            className={cn(
                                                'absolute left-[11px] top-6 w-0.5 h-[calc(100%-24px)]',
                                                isPast ? 'bg-status-success' : 'bg-line-default'
                                            )}
                                        />
                                    )}

                                    {/* Timeline dot */}
                                    <div 
                                        className={cn(
                                            'absolute left-0 top-1 w-6 h-6 rounded-full flex items-center justify-center border-2',
                                            isPast && 'bg-status-success border-status-success',
                                            isCurrent && 'bg-brand-red border-brand-red animate-pulse-glow',
                                            isFuture && 'bg-surface-card border-line-default'
                                        )}
                                    >
                                        {isPast ? (
                                            <CheckCircle className="w-3 h-3 text-white" />
                                        ) : isCurrent ? (
                                            <Circle className="w-2 h-2 text-white fill-white" />
                                        ) : (
                                            <Circle className="w-2 h-2 text-content-muted" />
                                        )}
                                    </div>

                                    {/* Content */}
                                    <div 
                                        className={cn(
                                            'p-5 rounded-lg border transition-all group',
                                            isPast && 'bg-surface-app border-status-success/30',
                                            isCurrent && 'bg-brand-red/5 border-brand-red/50',
                                            isFuture && 'bg-surface-app border-line-default opacity-60 hover:opacity-80'
                                        )}
                                    >
                                        <div className="flex items-start justify-between mb-3">
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    {isPast && (
                                                        <Badge className="badge badge-success">Completed</Badge>
                                                    )}
                                                    {isCurrent && (
                                                        <Badge className="badge badge-error">In Progress</Badge>
                                                    )}
                                                    {isFuture && (
                                                        <Badge className="badge badge-info">Upcoming</Badge>
                                                    )}
                                                </div>
                                                <h4 className="text-lg font-heading font-bold text-white">
                                                    {phase.name}
                                                </h4>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <div className="flex items-center gap-2 text-sm text-content-muted">
                                                    <Calendar className="w-4 h-4" />
                                                    Week {phase.week_start}-{phase.week_end}
                                                </div>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="opacity-0 group-hover:opacity-100 transition-opacity text-content-muted hover:text-brand-red"
                                                    onClick={() => {
                                                        setEditingPhaseId(phase.id);
                                                        setIsAddingPhase(false);
                                                    }}
                                                    data-testid={`edit-phase-${phase.id}-btn`}
                                                >
                                                    <Pencil className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>

                                        <p className="text-sm text-content-secondary mb-4">
                                            {phase.objective}
                                        </p>

                                        {/* Channels */}
                                        <div className="flex items-center gap-2 mb-4">
                                            {(phase.channels || []).map((channel) => {
                                                const Icon = channelIcons[channel] || Target;
                                                return (
                                                    <div 
                                                        key={channel}
                                                        className="flex items-center gap-1 px-2 py-1 rounded bg-surface-card text-xs text-content-secondary"
                                                    >
                                                        <Icon className="w-3 h-3" />
                                                        {channel}
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {/* Activities */}
                                        <div>
                                            <h5 className="text-xs font-bold text-content-muted uppercase mb-2">
                                                Activities
                                            </h5>
                                            <ul className="space-y-1">
                                                {(phase.activities || []).map((activity, i) => (
                                                    <li 
                                                        key={i}
                                                        className="text-sm text-content-secondary flex items-center gap-2"
                                                    >
                                                        <span className="w-1 h-1 rounded-full bg-content-muted" />
                                                        {activity}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {phases.length === 0 && !isAddingPhase && (
                        <div className="text-center py-12">
                            <Target className="w-12 h-12 text-content-muted mx-auto mb-3" />
                            <h4 className="text-lg font-heading font-bold text-white mb-2">No Phases Yet</h4>
                            <p className="text-content-secondary mb-4">Start building your ABM journey roadmap</p>
                            <Button
                                onClick={() => setIsAddingPhase(true)}
                                className="bg-brand-red hover:bg-brand-red/80 text-white font-bold gap-2"
                                data-testid="add-first-phase-btn"
                            >
                                <Plus className="w-4 h-4" />
                                Add First Phase
                            </Button>
                        </div>
                    )}
                </section>

                {saving && (
                    <div className="fixed bottom-6 right-6 flex items-center gap-2 bg-surface-card border border-line-default rounded-lg px-4 py-3 shadow-lg">
                        <div className="w-4 h-4 border-2 border-brand-red/30 border-t-brand-red rounded-full animate-spin" />
                        <span className="text-sm text-content-secondary">Saving...</span>
                    </div>
                )}
            </div>
        </div>
    );
}
